/**
 * MandantDsgvoSelfService — DSGVO-Self-Service-Page für Mandanten.
 *
 * Pfad: /mandant/case/:caseId/datenschutz
 * Guards: RequireAuth + RequireMandantAccessForCase (siehe App.tsx).
 *
 * Sechs Sektionen, jede mit eigener Aktion:
 *   1. Datenexport (Art. 15 DSGVO) — JSON-Download, optional Print-View
 *   2. Löschanfrage (Art. 17 DSGVO) — mit Doppel-Bestätigungs-Modal
 *   3. Einwilligungs-Widerruf (Art. 7 Abs. 3 DSGVO) — Liste + Widerruf-Button
 *   4. Status meiner DSGVO-Anfragen — Liste mit Frist-Countdown
 *   5. Weitere Rechte (Art. 16/18/20/21/77) — Erläuterung + sonstige Anfrage
 *   6. Footer — Verantwortlicher, DSB, Aufsichtsbehörde
 *
 * Demo-Mode: bei isDemoCaseId() zeigen wir DEMO_DSGVO_REQUESTS und
 * DEMO_CONSENT_RECORDS, alle Aktionen sind no-ops mit Toast — der Banner
 * oben weist explizit auf den Demo-Modus hin.
 *
 * WCAG:
 *   - Section-Hierarchie via <h2> + aria-labelledby pro <section>
 *   - Alle Modals nutzen useFocusTrap + ESC-Schließen
 *   - aria-live="polite" für Status-Toasts (Erfolg/Fehler nach Aktion)
 *   - Touch-Targets >= 44px, focus-visible-Ringe Brand-konform
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ChevronLeft, Download, Printer, Trash2, X } from 'lucide-react';
import { de } from '../lib/i18n/de';
import { useAuthOrSession } from '../lib/authOrSession';
import { useFocusTrap } from '../components/a11y/useFocusTrap';
import { getSupabaseClient } from '../lib/supabase';
import { DEMO_CONSENT_RECORDS, DEMO_DSGVO_REQUESTS, isDemoCaseId } from '../lib/demo/demoFixtures';
import {
  daysUntilDeadline,
  downloadCaseDataAsJsonFile,
  ensureConsentRecordsForCase,
  listConsentRecordsForCase,
  listDsgvoRequestsForCase,
  requestCaseDeletion,
  withdrawConsent,
} from '../lib/supabaseDsgvo';
import type { ConsentRecordRow, DsgvoRequestRow, DsgvoRequestType } from '../lib/supabaseTypes';
import { useToast } from '../components/ui/ToastProvider';

// ============================================================================
// Component
// ============================================================================

export function MandantDsgvoSelfService() {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { isMock } = useAuthOrSession();
  const isDemo = isMock || isDemoCaseId(caseId);

  const [requests, setRequests] = useState<ReadonlyArray<DsgvoRequestRow>>([]);
  const [consents, setConsents] = useState<ReadonlyArray<ConsentRecordRow>>([]);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [loadingConsents, setLoadingConsents] = useState(true);

  // Statusmeldungen laufen über das globale Toast-System (aria-live dort)
  const toast = useToast();
  const [exportRunning, setExportRunning] = useState(false);

  const refreshRequests = useCallback(async () => {
    setLoadingRequests(true);
    if (isDemo) {
      setRequests(DEMO_DSGVO_REQUESTS);
      setLoadingRequests(false);
      return;
    }
    try {
      const list = await listDsgvoRequestsForCase(caseId);
      setRequests(list);
    } catch (err) {
      console.warn('MandantDsgvoSelfService: listDsgvoRequestsForCase failed', err);
      setRequests([]);
    } finally {
      setLoadingRequests(false);
    }
  }, [caseId, isDemo]);

  // Defensive Auto-Repair: wenn die consent_records-Liste beim ersten Load
  // leer ist (Bestands-Case ohne provisioning, DB-Restore, Migration-Lücke),
  // rufen wir EINMAL ensureConsentRecordsForCase + reload. Ohne Ref-Guard
  // würde ein dauerhaft leeres Result zur Endlos-Schleife führen.
  const repairAttemptedRef = useRef(false);

  const refreshConsents = useCallback(async () => {
    setLoadingConsents(true);
    if (isDemo) {
      setConsents(DEMO_CONSENT_RECORDS);
      setLoadingConsents(false);
      return;
    }
    try {
      let list = await listConsentRecordsForCase(caseId);

      // Auto-Repair-Pfad: nur einmal pro Mount, nur im Live-Modus.
      if (list.length === 0 && !repairAttemptedRef.current) {
        repairAttemptedRef.current = true;
        try {
          await ensureConsentRecordsForCase(caseId);
          // Nach Provisioning erneut laden — beim zweiten Versuch sollten
          // die Records da sein. Sind sie es nicht (Edge-Case: kein Mandant-
          // Access verknüpft, Case nicht verfügbar), bleibt list leer und
          // die UI zeigt den „keine Einwilligungen"-Hinweis.
          list = await listConsentRecordsForCase(caseId);
        } catch (repairErr) {
          // Repair fehlgeschlagen → nur warnen, UI zeigt leere Liste.
          console.warn(
            'MandantDsgvoSelfService: ensureConsentRecordsForCase failed (repair)',
            repairErr,
          );
        }
      }

      setConsents(list);
    } catch (err) {
      console.warn('MandantDsgvoSelfService: listConsentRecordsForCase failed', err);
      setConsents([]);
    } finally {
      setLoadingConsents(false);
    }
  }, [caseId, isDemo]);

  useEffect(() => {
    void refreshRequests();
    void refreshConsents();
  }, [refreshRequests, refreshConsents]);

  // ------- Datenexport ----------------------------------------------------
  async function handleExport(): Promise<void> {
    if (isDemo) {
      toast.show(
        'Demo-Modus: Datenexport würde im Live-Betrieb als JSON-Datei heruntergeladen.',
        'success',
      );
      return;
    }
    setExportRunning(true);
    try {
      await downloadCaseDataAsJsonFile(caseId);
      toast.show(de.dsgvo.selfService.exportSuccess, 'success');
    } catch (err) {
      toast.show(err instanceof Error ? err.message : de.dsgvo.selfService.exportError, 'error');
    } finally {
      setExportRunning(false);
    }
  }

  function openPrintView(): void {
    if (typeof window === 'undefined') return;
    window.open(`/mandant/case/${caseId}/print?autoprint=1`, '_blank', 'noopener,noreferrer');
  }

  // ------- Löschanfrage --------------------------------------------------
  const openDeletionRequest = useMemo(
    () =>
      requests.find(
        (r) => r.request_type === 'deletion' && (r.status === 'open' || r.status === 'in_progress'),
      ) ?? null,
    [requests],
  );

  // ------- Sonstige-Anfrage-Modal ---------------------------------------
  const [otherModalOpen, setOtherModalOpen] = useState(false);

  return (
    <article className="pb-24 pt-8 md:pt-12">
      <Link
        to={`/mandant/case/${caseId}`}
        className="inline-flex min-h-[36px] items-center gap-1.5 font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
      >
        <ChevronLeft size={14} aria-hidden="true" />
        {de.dsgvo.selfService.backLink}
      </Link>

      <Hero />

      {isDemo && (
        <p
          role="status"
          className="mt-6 rounded-sm border border-amber-200 bg-amber-50 px-4 py-3 font-serif text-sm leading-relaxed text-amber-900 md:text-base"
        >
          <span className="font-sans text-xs font-medium uppercase tracking-widest text-amber-800">
            Demo-Modus ·{' '}
          </span>
          {de.dsgvo.selfService.demoBanner}
        </p>
      )}

      {/* 1. Datenexport */}
      <section
        className="mt-10 rounded-sm border border-stone-200 bg-white"
        aria-labelledby="dsgvo-export-title"
      >
        <SectionHeader
          label={de.dsgvo.selfService.exportLabel}
          title={de.dsgvo.selfService.exportTitle}
          intro={de.dsgvo.selfService.exportIntro}
          titleId="dsgvo-export-title"
        />
        <div className="px-6 py-5 md:px-10 md:py-6">
          <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
            {de.dsgvo.selfService.exportContains}
          </p>
          <ul className="mt-3 list-disc space-y-1 pl-5 font-serif text-sm text-stone-700">
            <li>{de.dsgvo.selfService.exportListItem1}</li>
            <li>{de.dsgvo.selfService.exportListItem2}</li>
            <li>{de.dsgvo.selfService.exportListItem3}</li>
            <li>{de.dsgvo.selfService.exportListItem4}</li>
            <li>{de.dsgvo.selfService.exportListItem5}</li>
            <li>{de.dsgvo.selfService.exportListItem6}</li>
          </ul>

          <div className="mt-6 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={() => {
                void handleExport();
              }}
              disabled={exportRunning}
              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-sm bg-stone-900 px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <Download size={14} aria-hidden="true" />
              {exportRunning
                ? de.dsgvo.selfService.exportButtonRunning
                : de.dsgvo.selfService.exportButton}
            </button>
            <button
              type="button"
              onClick={openPrintView}
              className="inline-flex min-h-[44px] flex-1 items-center justify-center gap-2 rounded-sm border border-stone-900 bg-white px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 transition-colors hover:bg-stone-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              <Printer size={14} aria-hidden="true" />
              {de.dsgvo.selfService.exportPrintButton}
            </button>
          </div>
          <p className="mt-3 font-serif text-xs italic text-stone-500">
            {de.dsgvo.selfService.exportPrintAlternative}
          </p>

          <p className="mt-5 rounded-sm border border-stone-100 bg-stone-50 px-3 py-2 font-serif text-xs italic text-stone-600">
            {de.dsgvo.selfService.exportNoteAuditLog}
          </p>
        </div>
      </section>

      {/* 2. Löschanfrage */}
      <section
        className="mt-8 rounded-sm border border-stone-200 bg-white"
        aria-labelledby="dsgvo-deletion-title"
      >
        <SectionHeader
          label={de.dsgvo.selfService.deletionLabel}
          title={de.dsgvo.selfService.deletionTitle}
          intro={de.dsgvo.selfService.deletionIntro}
          titleId="dsgvo-deletion-title"
        />
        <div className="px-6 py-5 md:px-10 md:py-6">
          {openDeletionRequest ? (
            <ExistingDeletionBlock request={openDeletionRequest} />
          ) : (
            <DeletionForm
              isDemo={isDemo}
              onSuccess={(req) => {
                toast.show(
                  de.dsgvo.selfService.deletionSuccess
                    .replace('{date}', formatDateTime(req.requested_at))
                    .replace('{deadline}', formatDate(req.legal_deadline_at)),
                  'success',
                );
                void refreshRequests();
              }}
              onError={(msg) => {
                toast.show(msg, 'error');
              }}
            />
          )}
        </div>
      </section>

      {/* 3. Einwilligungen widerrufen */}
      <section
        className="mt-8 rounded-sm border border-stone-200 bg-white"
        aria-labelledby="dsgvo-consent-title"
      >
        <SectionHeader
          label={de.dsgvo.selfService.consentLabel}
          title={de.dsgvo.selfService.consentTitle}
          intro={de.dsgvo.selfService.consentIntro}
          titleId="dsgvo-consent-title"
        />
        <div className="px-6 py-5 md:px-10 md:py-6">
          {loadingConsents ? (
            <p className="font-serif text-sm italic text-stone-500">Wird geladen …</p>
          ) : consents.length === 0 ? (
            <p className="font-serif text-sm italic text-stone-500">
              {de.dsgvo.selfService.consentEmpty}
            </p>
          ) : (
            <ul className="space-y-3">
              {consents.map((c) => (
                <ConsentRow
                  key={c.id}
                  consent={c}
                  isDemo={isDemo}
                  caseId={caseId}
                  onSuccess={() => {
                    toast.show(de.dsgvo.selfService.consentWithdrawSuccess, 'success');
                    void refreshConsents();
                    void refreshRequests();
                  }}
                  onError={(msg) => {
                    toast.show(msg, 'error');
                  }}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* 4. Status meiner DSGVO-Anfragen */}
      <section
        className="mt-8 rounded-sm border border-stone-200 bg-white"
        aria-labelledby="dsgvo-requests-title"
      >
        <SectionHeader
          label={de.dsgvo.selfService.requestsLabel}
          title={de.dsgvo.selfService.requestsTitle}
          intro={de.dsgvo.selfService.requestsIntro}
          titleId="dsgvo-requests-title"
        />
        <div className="px-6 py-5 md:px-10 md:py-6">
          {loadingRequests ? (
            <p className="font-serif text-sm italic text-stone-500">Wird geladen …</p>
          ) : requests.length === 0 ? (
            <p className="font-serif text-sm italic text-stone-500">
              {de.dsgvo.selfService.requestsEmpty}
            </p>
          ) : (
            <ul className="space-y-3">
              {requests.map((r) => (
                <DsgvoRequestRowMandant key={r.id} request={r} />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* 5. Weitere Rechte */}
      <section
        className="mt-8 rounded-sm border border-stone-200 bg-white"
        aria-labelledby="dsgvo-other-title"
      >
        <SectionHeader
          label={de.dsgvo.selfService.otherRightsLabel}
          title={de.dsgvo.selfService.otherRightsTitle}
          intro=""
          titleId="dsgvo-other-title"
        />
        <div className="space-y-5 px-6 py-5 md:px-10 md:py-6">
          <RightCard
            title={de.dsgvo.selfService.rectificationTitle}
            body={de.dsgvo.selfService.rectificationBody}
          />
          <RightCard
            title={de.dsgvo.selfService.restrictionTitle}
            body={de.dsgvo.selfService.restrictionBody}
          />
          <RightCard
            title={de.dsgvo.selfService.portabilityTitle}
            body={de.dsgvo.selfService.portabilityBody}
          />
          <RightCard
            title={de.dsgvo.selfService.objectionTitle}
            body={de.dsgvo.selfService.objectionBody}
          />
          <RightCard
            title={de.dsgvo.selfService.complaintTitle}
            body={de.dsgvo.selfService.complaintBody}
            link={{
              href: 'https://www.bfdi.bund.de/DE/Buergerinnen-und-Buerger/Beschwerde/beschwerde-node.html',
              label: de.dsgvo.selfService.complaintLink,
            }}
          />
          <div className="border-t border-stone-100 pt-5">
            <button
              type="button"
              onClick={() => {
                setOtherModalOpen(true);
              }}
              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm border border-stone-900 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-900 hover:bg-stone-900 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.dsgvo.selfService.otherRequestOpenButton}
            </button>
          </div>
        </div>
      </section>

      {/* 6. Footer */}
      <section className="mt-10 border-t border-stone-200 pt-8">
        <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.dsgvo.selfService.footerControllerLabel}
        </p>
        <p className="mt-2 font-serif text-sm leading-relaxed text-stone-700">
          {de.dsgvo.selfService.footerControllerAddress}
        </p>

        <p className="mt-6 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.dsgvo.selfService.footerDpoLabel}
        </p>
        <p className="mt-2 font-serif text-sm leading-relaxed text-stone-700">
          {de.dsgvo.selfService.footerDpoBody}
        </p>

        <p className="mt-6 font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
          {de.dsgvo.selfService.footerSupervisoryLabel}
        </p>
        <p className="mt-2 font-serif text-sm leading-relaxed text-stone-700">
          {de.dsgvo.selfService.footerSupervisoryBody}
        </p>
      </section>

      <div className="mt-10 border-t border-stone-200 pt-6">
        <button
          type="button"
          onClick={() => {
            void navigate(`/mandant/case/${caseId}`);
          }}
          className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          ← {de.dsgvo.selfService.backLink}
        </button>
      </div>

      {otherModalOpen && (
        <OtherRequestModal
          isDemo={isDemo}
          caseId={caseId}
          onClose={() => {
            setOtherModalOpen(false);
          }}
          onSuccess={() => {
            setOtherModalOpen(false);
            toast.show(de.dsgvo.selfService.otherRequestSuccess, 'success');
            void refreshRequests();
          }}
        />
      )}
    </article>
  );
}

// ============================================================================
// Hero / SectionHeader / RightCard
// ============================================================================

function Hero() {
  return (
    <header className="relative pb-6 pt-6 md:pb-10 md:pt-10">
      <div
        aria-hidden="true"
        className="absolute right-0 top-6 hidden h-24 w-24 rounded-sm md:block"
        style={{ backgroundColor: 'var(--color-dim-resources)', opacity: 0.18 }}
      />
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {de.dsgvo.selfService.sectionLabel}
      </p>
      <h1 className="mt-3 font-serif text-4xl font-black leading-none tracking-tight text-stone-900 md:text-6xl">
        {de.dsgvo.selfService.title}
      </h1>
      <p className="mt-3 max-w-prose font-serif text-base italic text-stone-500 md:mt-4 md:text-lg">
        {de.dsgvo.selfService.subtitle}
      </p>
    </header>
  );
}

function SectionHeader({
  label,
  title,
  intro,
  titleId,
}: {
  label: string;
  title: string;
  intro: string;
  titleId: string;
}) {
  return (
    <div className="border-b border-stone-200 px-6 py-5 md:px-10">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-stone-500">
        {label}
      </p>
      <h2 id={titleId} className="mt-2 font-serif text-2xl font-bold tracking-tight text-stone-900">
        {title}
      </h2>
      {intro.length > 0 && (
        <p className="mt-2 max-w-prose font-serif text-sm italic text-stone-600">{intro}</p>
      )}
    </div>
  );
}

function RightCard({
  title,
  body,
  link,
}: {
  title: string;
  body: string;
  link?: { readonly href: string; readonly label: string };
}) {
  return (
    <div>
      <h3 className="font-serif text-base font-bold tracking-tight text-stone-900">{title}</h3>
      <p className="mt-1 font-serif text-sm leading-relaxed text-stone-700">{body}</p>
      {link && (
        // Externer Link: bewusst KEIN target='_blank' (Eslint-Regel
        // no-restricted-syntax verbietet das wegen Reverse-Tab-Nabbing-Risiko).
        // Browser-Default: gleiche Tab — User kann mit ⌘/Ctrl+Klick selbst
        // entscheiden, ob er einen neuen Tab öffnen möchte.
        <a
          href={link.href}
          rel="noopener noreferrer"
          className="mt-2 inline-flex font-sans text-xs font-medium uppercase tracking-widest text-stone-700 underline-offset-2 hover:text-stone-900 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
        >
          {link.label} →
        </a>
      )}
    </div>
  );
}

// ============================================================================
// Löschanfrage-Form + Existing-Block
// ============================================================================

function ExistingDeletionBlock({ request }: { request: DsgvoRequestRow }) {
  return (
    <div className="rounded-sm border border-amber-200 bg-amber-50 px-4 py-4">
      <p className="font-sans text-xs font-medium uppercase tracking-widest text-amber-900">
        {de.dsgvo.selfService.deletionExistingTitle}
      </p>
      <p className="mt-2 font-serif text-sm text-amber-900">
        {de.dsgvo.selfService.deletionExistingHint
          .replace('{date}', formatDate(request.requested_at))
          .replace('{deadline}', formatDate(request.legal_deadline_at))}
      </p>
    </div>
  );
}

function DeletionForm({
  isDemo,
  onSuccess,
  onError,
}: {
  isDemo: boolean;
  onSuccess: (req: DsgvoRequestRow) => void;
  onError: (msg: string) => void;
}) {
  const { caseId = '' } = useParams<{ caseId: string }>();
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function submit(): Promise<void> {
    if (isDemo) {
      // Demo: künstlich erfolgreichen Eintrag bauen + Modal schließen
      setConfirmOpen(false);
      const now = new Date();
      const fakeReq: DsgvoRequestRow = {
        id: '00000000-3333-0000-0000-demo000000DE',
        case_id: caseId,
        request_type: 'deletion',
        requested_by: 'demo-mandant',
        requested_by_role: 'mandant',
        requested_at: now.toISOString(),
        reason: reason.trim().length > 0 ? reason.trim() : null,
        status: 'open',
        handled_by: null,
        handled_at: null,
        response_text: null,
        response_artifact_path: null,
        legal_deadline_at: new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        created_at: now.toISOString(),
        updated_at: now.toISOString(),
      };
      onSuccess(fakeReq);
      setReason('');
      return;
    }
    setSubmitting(true);
    try {
      const req = await requestCaseDeletion(caseId, reason.trim().length > 0 ? reason : undefined);
      setConfirmOpen(false);
      setReason('');
      onSuccess(req);
    } catch (err) {
      setConfirmOpen(false);
      onError(err instanceof Error ? err.message : de.dsgvo.selfService.deletionError);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <label
        htmlFor="dsgvo-deletion-reason"
        className="font-sans text-xs font-medium uppercase tracking-widest text-stone-600"
      >
        {de.dsgvo.selfService.deletionReasonLabel}
      </label>
      <textarea
        id="dsgvo-deletion-reason"
        value={reason}
        onChange={(e) => {
          setReason(e.target.value);
        }}
        placeholder={de.dsgvo.selfService.deletionReasonPlaceholder}
        rows={4}
        maxLength={2000}
        className="mt-2 w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
      />
      <p className="mt-1 text-right font-mono text-[10px] text-stone-500">
        {de.dsgvo.selfService.deletionReasonCharCount.replace('{count}', String(reason.length))}
      </p>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          onClick={() => {
            setConfirmOpen(true);
          }}
          disabled={submitting}
          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-sm bg-red-700 px-5 py-3 font-sans text-xs font-medium uppercase tracking-widest text-white transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-stone-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-700 focus-visible:ring-offset-2"
        >
          <Trash2 size={14} aria-hidden="true" />
          {submitting
            ? de.dsgvo.selfService.deletionButtonRunning
            : de.dsgvo.selfService.deletionButton}
        </button>
      </div>

      {confirmOpen && (
        <ConfirmModal
          title={de.dsgvo.selfService.deletionConfirmTitle}
          body={de.dsgvo.selfService.deletionConfirmBody}
          confirmLabel={de.dsgvo.selfService.deletionConfirmSubmit}
          cancelLabel={de.dsgvo.selfService.deletionConfirmCancel}
          confirmKind="danger"
          submitting={submitting}
          onConfirm={() => {
            void submit();
          }}
          onCancel={() => {
            setConfirmOpen(false);
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// Consent Row
// ============================================================================

function ConsentRow({
  consent,
  isDemo,
  caseId,
  onSuccess,
  onError,
}: {
  consent: ConsentRecordRow;
  isDemo: boolean;
  caseId: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const [working, setWorking] = useState(false);
  const isActive = consent.granted && consent.withdrawn_at === null;
  const purposeLabel = de.dsgvo.consentPurposes[consent.purpose] ?? consent.purpose;

  async function handleWithdraw(): Promise<void> {
    if (isDemo) {
      onSuccess();
      return;
    }
    setWorking(true);
    try {
      await withdrawConsent(caseId, consent.purpose);
      onSuccess();
    } catch (err) {
      onError(err instanceof Error ? err.message : de.dsgvo.selfService.consentWithdrawError);
    } finally {
      setWorking(false);
    }
  }

  return (
    <li className="rounded-sm border border-stone-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${
            isActive ? 'bg-emerald-100 text-emerald-800' : 'bg-stone-100 text-stone-600'
          }`}
        >
          {isActive
            ? de.dsgvo.selfService.consentStatusActive
            : de.dsgvo.selfService.consentStatusWithdrawn}
        </span>
        <p className="font-serif text-base font-bold tracking-tight text-stone-900">
          {purposeLabel}
        </p>
      </div>
      <p className="mt-1 font-serif text-xs italic text-stone-600">
        {de.dsgvo.selfService.consentGrantedAt.replace('{date}', formatDate(consent.granted_at))}
        {consent.withdrawn_at && (
          <>
            {' · '}
            {de.dsgvo.selfService.consentWithdrawnAt.replace(
              '{date}',
              formatDate(consent.withdrawn_at),
            )}
          </>
        )}
      </p>
      {isActive && (
        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              void handleWithdraw();
            }}
            disabled={working}
            className="inline-flex min-h-[36px] items-center rounded-sm border border-stone-900 bg-white px-3 py-1.5 font-sans text-[11px] font-medium uppercase tracking-widest text-stone-900 hover:bg-stone-900 hover:text-white disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            {working
              ? de.dsgvo.selfService.consentWithdrawing
              : de.dsgvo.selfService.consentWithdrawButton}
          </button>
        </div>
      )}
    </li>
  );
}

// ============================================================================
// DSGVO-Request-Row (Mandant-Sicht)
// ============================================================================

function DsgvoRequestRowMandant({ request }: { request: DsgvoRequestRow }) {
  const days = daysUntilDeadline(request.legal_deadline_at);
  const isOpen = request.status === 'open' || request.status === 'in_progress';

  return (
    <li className="rounded-sm border border-stone-200 bg-white px-4 py-3">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span
          className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${requestTypeBadge(request.request_type)}`}
        >
          {de.dsgvo.requestTypes[request.request_type]}
        </span>
        <span
          className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${statusBadge(request.status)}`}
        >
          {de.dsgvo.requestStatuses[request.status]}
        </span>
        {isOpen && (
          <span
            className={`inline-flex items-center rounded-sm px-2 py-0.5 font-sans text-[10px] font-medium uppercase tracking-widest ${
              days < 0
                ? 'bg-red-100 text-red-900'
                : days < 7
                  ? 'bg-amber-100 text-amber-900'
                  : 'bg-stone-100 text-stone-700'
            }`}
          >
            {days < 0
              ? de.dsgvo.selfService.requestDeadlineExpired
              : de.dsgvo.selfService.requestDeadlineDays.replace('{days}', String(days))}
          </span>
        )}
      </div>
      <p className="mt-2 font-serif text-xs italic text-stone-500">
        {de.dsgvo.selfService.requestRequestedAt.replace(
          '{date}',
          formatDateTime(request.requested_at),
        )}
        {' · '}
        {de.dsgvo.selfService.requestDeadline.replace(
          '{date}',
          formatDate(request.legal_deadline_at),
        )}
      </p>
      {request.reason && (
        <div className="mt-2 rounded-sm border-l-2 border-stone-200 bg-stone-50 px-3 py-2">
          <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-500">
            {de.dsgvo.selfService.requestReason}
          </p>
          <p className="mt-1 font-serif text-sm text-stone-800">{request.reason}</p>
        </div>
      )}
      {request.handled_at && (
        <p className="mt-2 font-serif text-xs italic text-stone-500">
          {de.dsgvo.selfService.requestHandledAt.replace(
            '{date}',
            formatDateTime(request.handled_at),
          )}
        </p>
      )}
      {request.response_text && (
        <div className="mt-2 rounded-sm border-l-2 border-stone-300 bg-stone-50 px-3 py-2">
          <p className="font-sans text-[10px] font-medium uppercase tracking-widest text-stone-600">
            {de.dsgvo.selfService.requestResponse}
          </p>
          <p className="mt-1 font-serif text-sm text-stone-800">{request.response_text}</p>
        </div>
      )}
    </li>
  );
}

// ============================================================================
// Other-Request-Modal (Sonstige Anfragen / Berichtigung / Einschränkung)
// ============================================================================

function OtherRequestModal({
  isDemo,
  caseId,
  onClose,
  onSuccess,
}: {
  isDemo: boolean;
  caseId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [text, setText] = useState('');
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { active: true });

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  async function submit(): Promise<void> {
    if (text.trim().length === 0) {
      setError('Bitte beschreiben Sie kurz, was Sie wünschen.');
      return;
    }
    if (isDemo) {
      onSuccess();
      return;
    }
    setWorking(true);
    setError(null);
    try {
      // Wir nutzen den 'rectification'-Type als Sammelbegriff für sonstige
      // Anfragen — die DB-Migration 0019 hat 4 fixe Types. Direktes INSERT,
      // weil die Library nur den deletion- und consent-Pfad als RPC kapselt.
      const sb = getSupabaseClient();
      const { data: u, error: uErr } = await sb.auth.getUser();
      if (uErr || !u.user) throw new Error('Nicht eingeloggt');
      const insertResult = await sb
        .from('dsgvo_requests')
        .insert({
          case_id: caseId,
          request_type: 'rectification',
          requested_by: u.user.id,
          reason: text.trim(),
        } as never)
        .select('*')
        .single();
      if (insertResult.error) throw insertResult.error;
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : de.dsgvo.selfService.otherRequestError);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dsgvo-other-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-lg rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between border-b border-stone-200 px-5 py-4 md:px-6">
          <h3
            id="dsgvo-other-modal-title"
            className="font-serif text-xl font-bold tracking-tight text-stone-900"
          >
            {de.dsgvo.selfService.otherRequestModalTitle}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={de.dsgvo.selfService.otherRequestCancel}
            className="rounded-sm p-1 text-stone-600 hover:bg-stone-100 hover:text-stone-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
          >
            <X size={16} aria-hidden="true" />
          </button>
        </div>
        <div className="px-5 py-5 md:px-6">
          <textarea
            value={text}
            onChange={(e) => {
              setText(e.target.value);
            }}
            placeholder={de.dsgvo.selfService.otherRequestPlaceholder}
            rows={6}
            maxLength={2000}
            aria-label={de.dsgvo.selfService.otherRequestModalTitle}
            className="w-full rounded-sm border border-stone-300 bg-white px-4 py-3 font-serif text-base text-stone-900 placeholder:italic placeholder:text-stone-400 focus:border-stone-900 focus:outline-none focus:ring-2 focus:ring-stone-900 focus:ring-offset-2"
          />
          {error && (
            <p
              role="alert"
              className="mt-3 rounded-sm border border-red-200 bg-red-50 px-3 py-2 font-serif text-sm text-red-800"
            >
              {error}
            </p>
          )}
          <div className="mt-4 flex flex-col items-stretch justify-end gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.dsgvo.selfService.otherRequestCancel}
            </button>
            <button
              type="button"
              disabled={working}
              onClick={() => {
                void submit();
              }}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm bg-stone-900 px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white hover:bg-stone-800 disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {de.dsgvo.selfService.otherRequestSubmit}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Confirm Modal (Generic — für Doppel-Bestätigung der Löschanfrage)
// ============================================================================

function ConfirmModal({
  title,
  body,
  confirmLabel,
  cancelLabel,
  confirmKind = 'normal',
  submitting,
  onConfirm,
  onCancel,
}: {
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  confirmKind?: 'normal' | 'danger';
  submitting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const dialogRef = useRef<HTMLDivElement>(null);
  useFocusTrap(dialogRef, { active: true });

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
    };
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-stone-900/40 px-4 py-10"
      role="dialog"
      aria-modal="true"
      aria-labelledby="confirm-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        ref={dialogRef}
        className="w-full max-w-md rounded-sm border border-stone-200 bg-white shadow-xl"
      >
        <div className="border-b border-stone-200 px-5 py-4 md:px-6">
          <h3
            id="confirm-modal-title"
            className="font-serif text-xl font-bold tracking-tight text-stone-900"
          >
            {title}
          </h3>
        </div>
        <div className="px-5 py-4 md:px-6">
          <p className="font-serif text-sm leading-relaxed text-stone-700">{body}</p>
          <div className="mt-5 flex flex-col items-stretch justify-end gap-2 sm:flex-row">
            <button
              type="button"
              onClick={onCancel}
              className="inline-flex min-h-[44px] items-center justify-center rounded-sm border border-stone-200 bg-white px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-stone-700 hover:bg-stone-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-900 focus-visible:ring-offset-2"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              disabled={submitting}
              onClick={onConfirm}
              className={`inline-flex min-h-[44px] items-center justify-center rounded-sm px-5 py-2 font-sans text-xs font-medium uppercase tracking-widest text-white disabled:cursor-not-allowed disabled:bg-stone-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
                confirmKind === 'danger'
                  ? 'bg-red-700 hover:bg-red-800 focus-visible:ring-red-700'
                  : 'bg-stone-900 hover:bg-stone-800 focus-visible:ring-stone-900'
              }`}
            >
              {submitting ? '…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================

function requestTypeBadge(type: DsgvoRequestType): string {
  switch (type) {
    case 'data_export':
      return 'bg-sky-100 text-sky-900';
    case 'deletion':
      return 'bg-red-100 text-red-900';
    case 'consent_withdrawal':
      return 'bg-amber-100 text-amber-900';
    case 'rectification':
      return 'bg-stone-100 text-stone-900';
  }
}

function statusBadge(status: DsgvoRequestRow['status']): string {
  switch (status) {
    case 'open':
      return 'bg-stone-100 text-stone-700';
    case 'in_progress':
      return 'bg-sky-100 text-sky-800';
    case 'fulfilled':
      return 'bg-emerald-100 text-emerald-800';
    case 'rejected':
      return 'bg-red-100 text-red-800';
    case 'expired':
      return 'bg-stone-200 text-stone-500';
  }
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('de-DE');
  } catch {
    return iso;
  }
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}
